import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Output, EventEmitter, HostListener } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { InputTextModule } from 'primeng/inputtext';
import { Customer } from '../../../models/customer.model';
import { CustomerService } from '../../../services/customer.service';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-customer-search',
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule
  ],
  templateUrl: './customer-search.component.html'
})
export class CustomerSearchComponent implements OnInit, OnDestroy {
  @Output() customerSelected = new EventEmitter<Customer>();

  search: string = '';
  searchResults: Customer[] = []; // Público para permitir acesso do componente pai
  selectedCustomerIndex: number = -1;
  isSearching: boolean = false;
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(
    private customerService: CustomerService
  ) {}

  ngOnInit(): void {
    this.setupCustomerSearch();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private setupCustomerSearch(): void {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      // Guarda de tamanho mínimo aplicada aqui (e não no emissor) para que o
      // distinctUntilChanged receba todas as transições do termo e não bloqueie
      // a próxima busca quando o usuário repete o mesmo valor após limpar.
      if (term && term.length >= 3) {
        this.searchCustomers(term);
      } else {
        this.searchResults = [];
        this.selectedCustomerIndex = -1;
      }
    });
  }

  /**
   * Captura eventos de teclado globalmente para navegação na lista de resultados
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Verificar se está em input ou textarea
    const target = event.target as HTMLElement;
    const isInputFocused = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
    
    // Se houver resultados de busca, permitir navegação com setas
    if(this.searchResults.length > 0 && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      // Permitir navegação quando o input está focado (digitando)
      if(isInputFocused) {
        event.preventDefault();
        if(event.key === "ArrowDown") {
          this.navigateDown();
        } else if(event.key === "ArrowUp") {
          this.navigateUp();
        }
      }
    }
  }

  /**
   * Método público para ser chamado pelo componente pai quando o input muda
   */
  onSearchInputChange(searchValue: string): void {
    this.search = searchValue;
    this.onSearchChange();
  }

  /**
   * Método público para ser chamado pelo componente pai quando uma tecla é pressionada
   */
  onSearchKeyup(event: any): void {
    // Se há resultados de busca e o usuário pressionar Enter, seleciona o cliente
    if(event.key === "Enter"){
      if(this.searchResults.length > 0 && this.selectedCustomerIndex >= 0){
        // Selecionar cliente da lista de busca
        const selectedCustomer = this.searchResults[this.selectedCustomerIndex];
        this.customerSelected.emit(selectedCustomer);
        this.clearSearch();
        return;
      }
    }
  }

  private onSearchChange(): void {
    const searchTerm = this.search.trim();

    // Sempre emitir no subject (inclusive termos curtos/vazios) para que o
    // distinctUntilChanged enxergue todas as transições e não bloqueie o
    // próximo termo de 3+ caracteres quando o usuário repete o mesmo valor
    // após limpar o input. A guarda de tamanho mínimo é aplicada no subscribe.
    this.searchSubject.next(searchTerm);

    // Limpar resultados imediatamente para feedback de UI quando o termo é curto.
    if(!searchTerm || searchTerm.length < 3){
      this.searchResults = [];
      this.selectedCustomerIndex = -1;
    }
  }

  private searchCustomers(searchTerm: string): void {
    this.isSearching = true;
    // Buscar apenas clientes ativos (active: true), página 1
    this.customerService.getAll(searchTerm, true, 1).subscribe({
      next: (response) => {
        this.searchResults = response.data || [];
        this.selectedCustomerIndex = this.searchResults.length > 0 ? 0 : -1;
        this.isSearching = false;
      },
      error: (err) => {
        this.searchResults = [];
        this.selectedCustomerIndex = -1;
        this.isSearching = false;
      }
    });
  }

  selectCustomerFromList(customer: Customer): void {
    this.customerSelected.emit(customer);
    this.clearSearch();
  }

  private navigateDown(): void {
    if(this.searchResults.length > 0){
      this.selectedCustomerIndex = Math.min(this.selectedCustomerIndex + 1, this.searchResults.length - 1);
    }
  }

  private navigateUp(): void {
    if(this.searchResults.length > 0){
      this.selectedCustomerIndex = Math.max(this.selectedCustomerIndex - 1, -1);
    }
  }

  private clearSearch(): void {
    this.search = '';
    this.searchResults = [];
    this.selectedCustomerIndex = -1;
    // Resetar o estado do distinctUntilChanged para que repetir o mesmo termo
    // após selecionar um cliente (Enter) dispare uma nova busca.
    this.searchSubject.next('');
  }

  /** Limpa busca e lista (ex.: após selecionar cliente no PDV/crediário). */
  resetSearch(): void {
    this.clearSearch();
  }
}

